import { safeAbsurd } from '@ipfabric/utils';
import { DiffLine, type DiffLineProps } from 'components/Diff/DiffLine';
import {
  type RowBlock,
  changesToRowBlocks,
  isChangedRowBlock,
  isUnchangedRowBlock,
} from 'components/Diff/helpers/changesToRowBlocks';
import type { Change } from 'diff';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { ReactNode } from 'react';

/**
 * Aligned block of diff lines
 *
 * Matters for changes (added/removed section) where next unchanged block has to be aligned next to each other
 */
type DiffLinePropsBlock = [oldText: DiffLineProps[], newText: DiffLineProps[]];

/**
 * Direction of the diff. By default, the diff is rendered from right to left, meaning the older version is on the right.
 * The visual impact is that lines present only in the older version are rendered as removed (red),
 * while lines present only in the newer version are rendered as added (green).
 */
export type DiffDirection = 'right-to-left' | 'left-to-right';

const rowBlocksToDiffLinePropsBlocks = (
  rowBlocks: RowBlock[],
): DiffLinePropsBlock[] => {
  let oldLineNumber = 1;
  let newLineNumber = 1;
  const rowBlockToDiffLinePropsBlock = (
    rowBlock: RowBlock,
  ): DiffLinePropsBlock => {
    const oldDiffLinesProps: DiffLineProps[] = [];
    const newDiffLinesProps: DiffLineProps[] = [];
    if (isUnchangedRowBlock(rowBlock)) {
      for (const line of rowBlock.unchanged) {
        oldDiffLinesProps.push({
          content: line,
          status: 'unchanged',
          lineNumber: oldLineNumber,
        });
        newDiffLinesProps.push({
          content: line,
          status: 'unchanged',
          lineNumber: newLineNumber,
        });
        oldLineNumber += 1;
        newLineNumber += 1;
      }
    } else if (isChangedRowBlock(rowBlock)) {
      for (const line of rowBlock.removed) {
        oldDiffLinesProps.push({
          content: line,
          status: 'removed',
          lineNumber: oldLineNumber,
        });
        oldLineNumber += 1;
      }
      for (const line of rowBlock.added) {
        newDiffLinesProps.push({
          content: line,
          status: 'added',
          lineNumber: newLineNumber,
        });
        newLineNumber += 1;
      }
    } else {
      safeAbsurd(rowBlock, 'Unexpected row block type');
    }
    return [oldDiffLinesProps, newDiffLinesProps];
  };

  return rowBlocks.map(rowBlockToDiffLinePropsBlock);
};

const renderChangeBlock =
  (direction: DiffDirection) =>
  (
    index: number,
    [oldDiffLinesProps, newDiffLinesProps]: DiffLinePropsBlock,
  ) => {
    const [left, right] =
      direction === 'left-to-right'
        ? [oldDiffLinesProps, newDiffLinesProps]
        : [newDiffLinesProps, oldDiffLinesProps];

    return (
      <div key={index} className='flex flex-row'>
        <div className='diff-sidebyside-left flex-1'>
          {left.map((diffLineProps, innerIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: no better key
            <DiffLine key={innerIndex} {...diffLineProps} />
          ))}
        </div>
        <div className='flex-1'>
          {right.map((diffLineProps, innerIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: no better key
            <DiffLine key={innerIndex} {...diffLineProps} />
          ))}
        </div>
      </div>
    );
  };

export const renderSideBySideChanges = (
  changes: Change[],
  direction: DiffDirection,
): ReactNode =>
  pipe(
    changes,
    changesToRowBlocks,
    (changes) => rowBlocksToDiffLinePropsBlocks(changes),
    array.mapWithIndex(renderChangeBlock(direction)),
  );
